import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  PlayArrowOutlined as ResumeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { Campaign } from '../types/outreach';

const OutreachCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit Dialog State
  const [editOpen, setEditOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [editName, setEditName] = useState('');
  const [editScheduleType, setEditScheduleType] = useState<'manual' | 'weekly_mon' | 'monthly_1st' | 'quarterly'>('manual');
  const [isSavingEdit, setIsSubmittingEdit] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await ApiService.getCampaigns();
      setCampaigns(result.campaigns || []);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      setError(err.message || 'Failed to fetch campaigns.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunCampaign = async (id: number) => {
    if (!window.confirm('Run this campaign group immediately? This will gather all matching domains and funnel them into staging.')) return;
    
    setError(null);
    try {
      const result = await ApiService.runCampaign(id);
      if (result.success && result.sessionId) {
        alert(result.message || 'Campaign executed successfully! Directing you to validation process.');
        navigate(`/outreach/process?session=${result.sessionId}`);
      } else {
        setError('Failed to start campaign run.');
      }
    } catch (err: any) {
      console.error('Error manual running campaign:', err);
      setError(err.message || 'Failed to run campaign group.');
    }
  };

  const handleToggleStatus = async (campaign: Campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      const response = await ApiService.updateCampaign(campaign.id, {
        status: newStatus,
      });
      if (response.success) {
        fetchCampaigns();
      }
    } catch (err: any) {
      console.error('Error toggling campaign status:', err);
      setError(err.message || 'Failed to toggle campaign status.');
    }
  };

  const handleDeleteCampaign = async (id: number) => {
    if (!window.confirm('Are you sure you want to permanently delete this campaign scheduler group? This action cannot be undone.')) return;

    try {
      const response = await ApiService.deleteCampaign(id);
      if (response.success) {
        fetchCampaigns();
      }
    } catch (err: any) {
      console.error('Error deleting campaign:', err);
      setError(err.message || 'Failed to delete campaign.');
    }
  };

  const handleOpenEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setEditName(campaign.name);
    setEditScheduleType(campaign.schedule_type);
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedCampaign(null);
    setEditOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedCampaign) return;
    if (!editName.trim()) {
      alert('Name is required.');
      return;
    }

    setIsSubmittingEdit(true);
    setError(null);
    try {
      const response = await ApiService.updateCampaign(selectedCampaign.id, {
        name: editName.trim(),
        schedule_type: editScheduleType,
      });

      if (response.success) {
        handleCloseEdit();
        fetchCampaigns();
      } else {
        setError('Failed to update campaign.');
      }
    } catch (err: any) {
      console.error('Error editing campaign:', err);
      setError(err.message || 'Failed to update campaign.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScheduleLabel = (type: Campaign['schedule_type']) => {
    switch (type) {
      case 'weekly_mon': return 'Weekly (Mondays)';
      case 'monthly_1st': return 'Monthly (1st of month)';
      case 'quarterly': return 'Quarterly (Every 3 months)';
      case 'manual':
      default:
        return 'Manual Run Only';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading active campaigns...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          ⏰ Scheduled Campaigns & Schedulers ({campaigns.length})
        </Typography>
        <IconButton onClick={fetchCampaigns} size="small" color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {campaigns.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary', backgroundColor: '#fcfdfd', borderRadius: '12px', border: '1px dashed #ddd' }}>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 'medium' }}>
            No saved Campaign Schedulers found
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create a campaign group by defining criteria in the <strong>Lead Finder</strong> tab first.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} sm={6} md={4} key={campaign.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: '12px',
                border: '1px solid #eaeaea',
                boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.06)'
                }
              }}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Header Status Badge */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', wordBreak: 'break-word', pr: 1 }}>
                      {campaign.name}
                    </Typography>
                    <Chip
                      label={campaign.status.toUpperCase()}
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.65rem',
                        height: 20,
                        color: '#ffffff',
                        background: campaign.status === 'active'
                          ? 'linear-gradient(135deg, #11b756 0%, #0c8a3c 100%)'
                          : campaign.status === 'paused'
                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                      }}
                    />
                  </Box>

                  <Typography color="text.secondary" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <ScheduleIcon sx={{ fontSize: 16 }} /> {getScheduleLabel(campaign.schedule_type)}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#fcfcfc', p: 1.5, borderRadius: '8px', border: '1px solid #f3f3f3' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Matching Domains:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{campaign.domains_count}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                        <DateIcon sx={{ fontSize: 12 }} /> Last Run:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'medium' }}>{formatDate(campaign.last_run_at)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                        <DateIcon sx={{ fontSize: 12 }} /> Next Run:
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: campaign.status === 'active' ? 'primary.main' : 'inherit' }}>
                        {campaign.status === 'active' ? formatDate(campaign.next_run_at) : 'Paused'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #fcfcfc', pt: 1 }}>
                  {/* Status Toggle & Edit Buttons */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={campaign.status === 'active' ? 'Pause Scheduler' : 'Activate Scheduler'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(campaign)}
                        color={campaign.status === 'active' ? 'warning' : 'success'}
                      >
                        {campaign.status === 'active' ? <PauseIcon /> : <ResumeIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Campaign Settings">
                      <IconButton size="small" onClick={() => handleOpenEdit(campaign)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Campaign">
                      <IconButton size="small" color="error" onClick={() => handleDeleteCampaign(campaign.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Immediate Manual Run trigger */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RunIcon />}
                    onClick={() => handleRunCampaign(campaign.id)}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      borderRadius: '6px'
                    }}
                  >
                    Run Now
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Scheduler Dialog */}
      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Campaign Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              size="small"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="edit-schedule-label">Schedule Interval</InputLabel>
              <Select
                labelId="edit-schedule-label"
                value={editScheduleType}
                label="Schedule Interval"
                onChange={(e) => setEditScheduleType(e.target.value as any)}
              >
                <MenuItem value="manual">Manual Run Only</MenuItem>
                <MenuItem value="weekly_mon">Weekly (Mondays at 8 AM)</MenuItem>
                <MenuItem value="monthly_1st">Monthly (1st of Month)</MenuItem>
                <MenuItem value="quarterly">Quarterly (Every 3 Months)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseEdit} color="inherit" disabled={isSavingEdit}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={isSavingEdit || !editName.trim()}
            startIcon={isSavingEdit && <CircularProgress size={16} color="inherit" />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutreachCampaignsPage;
